/**
 * Studio configs: configs, studio templates and scratch copies
 * (docs/wiring-documentation-proposal.md, "Configs, templates and scratch
 * copies", "Lock and pushes").
 *
 * - New configs are copies (`copy`): of a template, or of any config, also
 *   of another studio or project. There is no empty config.
 * - Templates change only through "Save to template" from a scratch copy
 *   (`saveToOriginal`).
 * - The active config is locked for the parts the bridge owns
 *   (`BRIDGE_OWNED_CONFIG_KEYS`) until another config is loaded or an admin
 *   force-unlocks it while the bridge is offline.
 * - A write never overwrites a version that was loaded, and a write based
 *   on an older version becomes a conflict version (`planConfigWrite`).
 * - Every mutator locks the studio row first (server), like the API's load
 *   reports and pushes, so lock checks and version numbers don't race them.
 *
 * Loads are not mutators: `POST /api/studios/:id/load` creates the pending
 * load (and the scratch copy) and rings the bridge.
 */
import { describeDomains } from "../operation-meta.js";
import { copyStudioConfigInputSchema, deleteStudioConfigInputSchema, forceUnlockStudioConfigInputSchema, idQueryArgsSchema, keepStudioScratchInputSchema, removeStudioConfigGearInputSchema, playoutRunLogsByConfigQueryArgsSchema, playoutRunLogsByProjectQueryArgsSchema, projectIDQueryArgsSchema, resolveStudioConfigConflictInputSchema, restoreStudioConfigVersionInputSchema, saveStudioScratchToOriginalInputSchema, setDefaultStudioTemplateInputSchema, setStudioConfigLmIoServerInputSchema, setStudioConfigVmixOutputInputSchema, studioConfigIDQueryArgsSchema, studioConfigLoadsQueryArgsSchema, studioIDQueryArgsSchema, updateStudioConfigInputSchema, } from "../projects-input-schemas.js";
import { ConflictError, ForbiddenError, NotFoundError, ValidationError, } from "../../errors.js";
import { canReadProject, isStaff, scopeToReadableProjects, } from "../project-access.js";
import { assertAdminDataWriteAccess, assertProjectWriteAccess, } from "../projects-helpers.js";
import { deterministicUUID, sortDeviceTree } from "../../types/wiring.js";
import { effectiveConfigIDOf, planScopedCleanup, planScopedGearClone, } from "../../types/effective-wiring.js";
import { bridgeOnlineState } from "../../types/bridge.js";
import { setVmixOutput, vmixOutputsOf, } from "../../types/vmix-outputs.js";
import { autoMapDevices, changedBridgeOwnedKeys, CONFIG_LOCK_MESSAGE, isConfigLocked, planConfigWrite, rewriteConfigReferences, isConfigLoadOpen, } from "../../types/config-loads.js";
import { configMediaHashes, mediaS3Key } from "../../types/media.js";
import { PLAYOUT_RUN_LOGS_PAGE } from "../../types/playout-log.js";
import { createChangeLog, loadInstallation, lockStudios, studioEffectiveConfigID, } from "./studio-installation.js";
/** Latest load rows shown on the studio page. */
export const STUDIO_CONFIG_LOADS_PAGE = 20;
/**
 * Scope loads to those requesting a config of a project the caller may
 * read (the loaded scratch copy has no project). Global configs and
 * templates stay staff-only.
 */
function scopeLoadsToReadableProjects(ctx, query) {
    if (isStaff(ctx))
        return query;
    if (ctx.role !== "freelancer")
        return query.where("id", "__unauthorized__");
    if (ctx.clientSide)
        return query;
    const ids = ctx.readableProjectIDs ?? [];
    if (ids.length === 0)
        return query.where("id", "__unauthorized__");
    return query.whereExists("requestedConfig", (config) => config.where("projectID", "IN", [...ids]));
}
export function createStudioConfigQueries({ defineProjectQuery, projectZql, }) {
    return {
        studioConfigs: {
            byStudio: defineProjectQuery(studioIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.studioConfigs)
                    .where("studioID", args.studioID)
                    .related("project")
                    .orderBy("updatedAt", "desc");
            }),
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.studioConfigs.where("id", "__unauthorized__");
                }
                return projectZql.studioConfigs
                    .where("projectID", args.projectID)
                    .related("studio")
                    .orderBy("updatedAt", "desc");
            }),
            byID: defineProjectQuery(idQueryArgsSchema, ({ args, ctx }) => {
                return scopeToReadableProjects(ctx, projectZql.studioConfigs)
                    .where("id", args.id)
                    .related("sourceConfig")
                    .one();
            }),
            versions: defineProjectQuery(studioConfigIDQueryArgsSchema, ({ args, ctx, }) => {
                const query = projectZql.studioConfigVersions.where("configID", args.configID);
                return (isStaff(ctx)
                    ? query
                    : query.whereExists("config", (config) => scopeToReadableProjects(ctx, config))).orderBy("version", "desc");
            }),
        },
        studioConfigLoads: {
            byStudio: defineProjectQuery(studioConfigLoadsQueryArgsSchema, ({ args, ctx, }) => {
                return scopeLoadsToReadableProjects(ctx, projectZql.studioConfigLoads)
                    .where("studioID", args.studioID)
                    .related("config")
                    .related("requestedConfig")
                    .related("requestedByPerson")
                    .orderBy("createdAt", "desc")
                    .limit(args.limit ?? STUDIO_CONFIG_LOADS_PAGE);
            }),
            byID: defineProjectQuery(idQueryArgsSchema, ({ args, ctx }) => {
                return scopeLoadsToReadableProjects(ctx, projectZql.studioConfigLoads)
                    .where("id", args.id)
                    .related("config")
                    .one();
            }),
        },
        mediaAssets: {
            byStudio: defineProjectQuery(studioIDQueryArgsSchema, ({ args, ctx, }) => {
                if (!isStaff(ctx)) {
                    return projectZql.mediaAssets.where("id", "__unauthorized__");
                }
                return projectZql.mediaAssets
                    .where("studioID", args.studioID)
                    .orderBy("createdAt", "desc");
            }),
        },
        playoutRunLogs: {
            byProject: defineProjectQuery(playoutRunLogsByProjectQueryArgsSchema, ({ args, ctx, }) => {
                if (!canReadProject(ctx, args.projectID)) {
                    return projectZql.playoutRunLogs.where("id", "__unauthorized__");
                }
                return projectZql.playoutRunLogs
                    .where("projectID", args.projectID)
                    .orderBy("plannedAt", "desc")
                    .limit(args.limit ?? PLAYOUT_RUN_LOGS_PAGE);
            }),
            byConfig: defineProjectQuery(playoutRunLogsByConfigQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.playoutRunLogs)
                    .where("configID", args.configID)
                    .orderBy("plannedAt", "desc")
                    .limit(args.limit ?? PLAYOUT_RUN_LOGS_PAGE);
            }),
        },
    };
}
/**
 * Media is shared by hash, so a copy to another studio copies no bytes: the
 * target studio gets `media_assets` rows for the same S3 objects.
 */
async function shareMediaWithStudio(tx, projectZql, input) {
    if (input.hashes.length === 0)
        return;
    const [sourceRows, targetRows] = await Promise.all([
        tx.run(projectZql.mediaAssets.where("studioID", input.sourceStudioID)),
        tx.run(projectZql.mediaAssets.where("studioID", input.targetStudioID)),
    ]);
    const present = new Set((targetRows ?? []).map((row) => row.sha256));
    const bySha = new Map((sourceRows ?? []).map((row) => [row.sha256, row]));
    for (const sha256 of input.hashes) {
        const row = bySha.get(sha256);
        if (!row || present.has(sha256))
            continue;
        await tx.mutate.mediaAssets.insert({
            id: deterministicUUID("media-asset", input.targetStudioID, sha256),
            studioID: input.targetStudioID,
            sha256,
            kind: row.kind,
            width: row.width ?? null,
            height: row.height ?? null,
            videoMode: row.videoMode ?? null,
            sizeBytes: row.sizeBytes,
            contentType: row.contentType,
            // Content-addressed: never trust a stored key (DB CHECK enforces it)
            s3Key: mediaS3Key(sha256),
            name: row.name ?? null,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        });
        present.add(sha256);
    }
}
function versionID(configID, version) {
    return deterministicUUID("studio-config-version", configID, String(version));
}
function toVersionInfo(row) {
    return {
        id: row.id,
        version: Number(row.version),
        source: row.source === "bridge" ? "bridge" : "cloud",
        createdAt: Number(row.createdAt ?? 0),
        loadedAt: row.loadedAt ?? null,
        conflictBaseVersion: row.conflictBaseVersion ?? null,
        conflictResolvedAt: row.conflictResolvedAt ?? null,
    };
}
/** Newest versions of a config, newest first (enough for the push rules). */
async function recentVersions(tx, projectZql, configID) {
    const rows = await tx.run(projectZql.studioConfigVersions
        .where("configID", configID)
        .orderBy("version", "desc")
        .limit(50));
    return [...(rows ?? [])].sort((a, b) => Number(b.version) - Number(a.version));
}
/**
 * Write config data edited in the cloud as a version (`planConfigWrite`:
 * consecutive cloud edits merge, a loaded version is never overwritten).
 * Returns the config row fields to update.
 */
async function writeCloudConfigData(tx, projectZql, config, configData) {
    const versions = await recentVersions(tx, projectZql, config.id);
    const plan = planConfigWrite({
        source: "cloud",
        headVersion: Number(config.version),
        baseVersion: null,
        versions: versions.map(toVersionInfo),
        now: Date.now(),
    });
    if (plan.kind === "coalesce") {
        await tx.mutate.studioConfigVersions.update({
            id: plan.versionID,
            configData,
        });
        return { configData };
    }
    await tx.mutate.studioConfigVersions.insert({
        id: versionID(config.id, plan.version),
        configID: config.id,
        version: plan.version,
        configData,
        source: "cloud",
        baseVersion: Number(config.version),
        conflictBaseVersion: null,
        conflictResolvedAt: null,
        loadedAt: null,
        createdAt: Date.now(),
    });
    return { configData, version: plan.version };
}
async function getConfig(tx, projectZql, id) {
    const config = await tx.run(projectZql.studioConfigs.where("id", id).one());
    if (!config)
        throw new NotFoundError("Studio config not found");
    return config;
}
/** Lock the config's studio, then read the config (see `lockStudios`). */
async function getLockedConfig(tx, projectZql, id, studioID) {
    await lockStudios(tx, [studioID]);
    const config = await getConfig(tx, projectZql, id);
    if (config.studioID === studioID)
        return config;
    await lockStudios(tx, [config.studioID]);
    return getConfig(tx, projectZql, id);
}
async function getStudio(tx, projectZql, id) {
    return tx.run(projectZql.studios.where("id", id).one());
}
function assertAdmin(ctx, what) {
    if (ctx.role !== "admin") {
        throw new ForbiddenError(`Only admins can ${what}`);
    }
}
/** Staff, or a freelancer whose project access is checked per config. */
function assertConfigEditor(ctx) {
    if (!isStaff(ctx) && ctx.role !== "freelancer") {
        assertAdminDataWriteAccess(ctx);
    }
}
/**
 * Staff edit every config. Collaborators with edit rights only configs of
 * their projects; templates, scratch copies and global configs stay staff's.
 */
async function assertConfigWriteAccess(tx, ctx, config) {
    if (isStaff(ctx))
        return;
    if (config.kind !== "config" || !config.projectID) {
        throw new ForbiddenError("Not allowed to edit this config");
    }
    await assertProjectWriteAccess(tx, ctx, config.projectID);
}
/**
 * Clone the source config's temporary gear (devices, ports) and re-patches
 * into the new config, in the same transaction as the copy. Returns the
 * plan, whose device and port maps rewrite the copied config data.
 */
function planCopyGear(input) {
    return planScopedGearClone({
        source: input.source,
        sourceConfigID: input.sourceConfigID,
        targetConfigID: input.targetConfigID,
        targetStudioID: input.targetStudioID,
        ...(input.target
            ? { targetPorts: input.target.ports, deviceMap: input.deviceMap }
            : {}),
    });
}
async function insertClonedGear(tx, log, studioID, clone) {
    const now = Date.now();
    // Parents before children (parent_id is a foreign key)
    for (const { device } of sortDeviceTree(clone.devices)) {
        const row = { ...device, createdAt: now, updatedAt: now };
        await tx.mutate.studioConnections.insert(row);
        log.add(studioID, "device", row.id, "create", null, row);
    }
    for (const port of clone.ports) {
        const row = { ...port, createdAt: now, updatedAt: now };
        await tx.mutate.studioPorts.insert(row);
        log.add(studioID, "port", row.id, "create", null, row);
    }
    for (const link of clone.links) {
        const row = { ...link, createdAt: now, updatedAt: now };
        await tx.mutate.studioLinks.insert(row);
        log.add(studioID, "link", row.id, "create", null, row);
    }
}
export function createStudioConfigMutators({ defineProjectMutator, projectZql, }) {
    /** Insert a new config with version 1. */
    async function insertConfig(tx, row) {
        const now = Date.now();
        await tx.mutate.studioConfigs.insert({
            id: row.id,
            studioID: row.studioID,
            projectID: row.projectID,
            kind: row.kind,
            name: row.name,
            description: row.description,
            isDefault: row.isDefault,
            sourceConfigID: row.sourceConfigID ?? null,
            sourceVersion: row.sourceVersion ?? null,
            kept: false,
            lastLoadedAt: null,
            version: 1,
            configData: row.configData,
            createdAt: now,
            updatedAt: now,
        });
        await tx.mutate.studioConfigVersions.insert({
            id: versionID(row.id, 1),
            configID: row.id,
            version: 1,
            configData: row.configData,
            source: "cloud",
            baseVersion: null,
            conflictBaseVersion: null,
            conflictResolvedAt: null,
            loadedAt: null,
            createdAt: now,
        });
    }
    /** Delete what a clean-up plan removes, with audit rows. */
    async function removeGear(tx, ctx, studioID, installation, plan) {
        if (plan.linksToDelete.length + plan.devicesToDelete.length === 0)
            return;
        const log = createChangeLog(tx, ctx, projectZql);
        const portIDs = new Set(plan.portIDsToDelete);
        const deleted = new Set();
        for (const link of [
            ...plan.linksToDelete,
            // Links on removed ports (all of this config, see validateLink)
            ...installation.links.filter((link) => portIDs.has(link.fromPortID) || portIDs.has(link.toPortID)),
        ]) {
            if (deleted.has(link.id))
                continue;
            deleted.add(link.id);
            await tx.mutate.studioLinks.delete({ id: link.id });
            log.add(studioID, "link", link.id, "delete", link, null);
        }
        for (const port of installation.ports) {
            if (!portIDs.has(port.id))
                continue;
            await tx.mutate.studioPorts.delete({ id: port.id });
            log.add(studioID, "port", port.id, "delete", port, null);
        }
        // Leaves first, so no child points at a deleted parent
        const order = sortDeviceTree(plan.devicesToDelete).reverse();
        for (const { device } of order) {
            await tx.mutate.studioConnections.delete({ id: device.id });
            log.add(studioID, "device", device.id, "delete", device, null);
        }
        await log.commit();
    }
    /** Make `version` the config's current version with this data. */
    async function insertVersion(tx, config, version, configData, extra) {
        const now = Date.now();
        await tx.mutate.studioConfigVersions.insert({
            id: versionID(config.id, version),
            configID: config.id,
            version,
            configData,
            source: "cloud",
            baseVersion: extra.baseVersion ?? null,
            conflictBaseVersion: extra.conflictBaseVersion ?? null,
            conflictResolvedAt: null,
            loadedAt: null,
            createdAt: now,
        });
        if (extra.conflictBaseVersion == null) {
            await tx.mutate.studioConfigs.update({
                id: config.id,
                configData,
                version,
                updatedAt: now,
            });
        }
    }
    return {
        studioConfigs: {
            copy: defineProjectMutator({
                input: copyStudioConfigInputSchema,
                description: "Create a config or studio template as a copy of an existing config, template or scratch copy (latest version unless sourceVersion is given), in the same or another studio. Its temporary gear and re-patches are cloned with it, and port references in the config data point at the clones. Device states are mapped to the target studio's devices (by name and driver unless deviceMap is given).",
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                if (!isStaff(ctx)) {
                    if (args.kind !== "config" || !args.projectID) {
                        throw new ForbiddenError("Not allowed to edit this project");
                    }
                    await assertProjectWriteAccess(tx, ctx, args.projectID);
                }
                const unlocked = await getConfig(tx, projectZql, args.sourceConfigID);
                if (!isStaff(ctx) &&
                    !(unlocked.kind === "config" &&
                        canReadProject(ctx, unlocked.projectID))) {
                    throw new NotFoundError("Studio config not found");
                }
                await lockStudios(tx, [unlocked.studioID, args.studioID]);
                const source = await getConfig(tx, projectZql, args.sourceConfigID);
                const target = await getStudio(tx, projectZql, args.studioID);
                if (!target)
                    throw new NotFoundError("Studio not found");
                let data = source.configData;
                if (args.sourceVersion !== undefined) {
                    const row = await tx.run(projectZql.studioConfigVersions
                        .where("configID", source.id)
                        .where("version", args.sourceVersion)
                        .one());
                    if (!row)
                        throw new NotFoundError("Config version not found");
                    data = row.configData;
                }
                const sameStudio = source.studioID === args.studioID;
                const sourceInstallation = await loadInstallation(tx, projectZql, source.studioID);
                const targetInstallation = sameStudio
                    ? undefined
                    : await loadInstallation(tx, projectZql, args.studioID);
                // Permanent devices of another studio: by name and driver unless
                // given. Temporary gear is cloned instead.
                let deviceMap = args.deviceMap
                    ? new Map(Object.entries(args.deviceMap))
                    : undefined;
                if (targetInstallation && !deviceMap) {
                    deviceMap = autoMapDevices(sourceInstallation.devices.filter((d) => !d.configID), targetInstallation.devices.filter((d) => !d.configID));
                }
                // A copy of a scratch copy brings the gear in effect: its original's
                const clone = planCopyGear({
                    source: sourceInstallation,
                    sourceConfigID: effectiveConfigIDOf(source) ?? source.id,
                    targetConfigID: args.id,
                    targetStudioID: args.studioID,
                    target: targetInstallation,
                    deviceMap,
                });
                const rewritten = rewriteConfigReferences(data, {
                    portMap: clone.portMap,
                    deviceMap: new Map([...(deviceMap ?? []), ...clone.deviceMap]),
                    keepUnmapped: sameStudio,
                });
                let isDefault = false;
                if (args.kind === "template") {
                    const templates = await tx.run(projectZql.studioConfigs
                        .where("studioID", args.studioID)
                        .where("kind", "template"));
                    isDefault = !(templates ?? []).some((t) => t.isDefault);
                }
                await insertConfig(tx, {
                    id: args.id,
                    studioID: args.studioID,
                    projectID: args.kind === "config" ? (args.projectID ?? null) : null,
                    kind: args.kind,
                    name: args.name,
                    description: args.description ?? null,
                    isDefault,
                    configData: rewritten.data,
                });
                if (clone.devices.length + clone.links.length > 0) {
                    const log = createChangeLog(tx, ctx, projectZql);
                    await insertClonedGear(tx, log, args.studioID, clone);
                    await log.commit();
                }
                if (!sameStudio) {
                    await shareMediaWithStudio(tx, projectZql, {
                        hashes: configMediaHashes(rewritten.data),
                        sourceStudioID: source.studioID,
                        targetStudioID: args.studioID,
                    });
                }
            }),
            update: defineProjectMutator({
                input: updateStudioConfigInputSchema,
                description: "Rename a config, edit its notes, assign it to a project, or edit its config data. The active config's bridge-owned parts (layouts, device states, Dante, media pool, playout) are locked; templates change only through a scratch copy.",
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                const config = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                await assertConfigWriteAccess(tx, ctx, config);
                if (!isStaff(ctx) &&
                    args.projectID !== undefined &&
                    args.projectID !== config.projectID) {
                    // Moving to another project needs edit rights there too
                    if (!args.projectID) {
                        throw new ForbiddenError("Not allowed to edit this config");
                    }
                    await assertProjectWriteAccess(tx, ctx, args.projectID);
                }
                const updates = {
                    id: args.id,
                    updatedAt: Date.now(),
                };
                if (args.name !== undefined)
                    updates.name = args.name;
                if (args.description !== undefined) {
                    updates.description = args.description ?? null;
                }
                if (args.projectID !== undefined) {
                    if (args.projectID && config.kind !== "config") {
                        throw new ValidationError("Only configs belong to a project, not templates or scratch copies");
                    }
                    updates.projectID = args.projectID ?? null;
                }
                if (args.configData !== undefined) {
                    if (config.kind === "template") {
                        throw new ValidationError("Templates only change through “Save to template” from a scratch copy. Load the template as a copy to change it.");
                    }
                    const studio = await getStudio(tx, projectZql, config.studioID);
                    if (isConfigLocked(studio, config.id) &&
                        changedBridgeOwnedKeys(config.configData, args.configData)
                            .length > 0) {
                        throw new ConflictError(CONFIG_LOCK_MESSAGE);
                    }
                    Object.assign(updates, await writeCloudConfigData(tx, projectZql, config, args.configData));
                }
                await tx.mutate.studioConfigs.update(updates);
            }),
            setLmIoServer: defineProjectMutator({
                input: setStudioConfigLmIoServerInputSchema,
                description: "Set a config's lm-io-server setup: the sources it captures and the outputs it plays, by capture card port ID. Documentation only (entered in lm-io-server by hand); editable while the config is active.",
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                const config = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                await assertConfigWriteAccess(tx, ctx, config);
                if (config.kind === "template") {
                    throw new ValidationError("Templates only change through “Save to template” from a scratch copy. Load the template as a copy to change it.");
                }
                // Only this key changes, on top of the latest data: a bridge push
                // in between keeps its parts, and the lock doesn't apply
                const configData = {
                    ...(config.configData ?? {}),
                    lm_io_server: args.lmIoServer,
                };
                await tx.mutate.studioConfigs.update({
                    id: config.id,
                    updatedAt: Date.now(),
                    ...(await writeCloudConfigData(tx, projectZql, config, configData)),
                });
            }),
            setVmixOutput: defineProjectMutator({
                input: setStudioConfigVmixOutputInputSchema,
                description: "Set which vMix output (Output 1-4, External 1-2) a DeckLink port of a config shows, or clear it with null. Used by signal tracing; editable while the config is active.",
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                const config = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                await assertConfigWriteAccess(tx, ctx, config);
                if (config.kind === "template") {
                    throw new ValidationError("Templates only change through “Save to template” from a scratch copy. Load the template as a copy to change it.");
                }
                const current = (config.configData ?? {});
                const configData = {
                    ...current,
                    vmix_outputs: setVmixOutput(vmixOutputsOf(current), args.portID, args.output),
                };
                await tx.mutate.studioConfigs.update({
                    id: config.id,
                    updatedAt: Date.now(),
                    ...(await writeCloudConfigData(tx, projectZql, config, configData)),
                });
            }),
            delete: defineProjectMutator({
                input: deleteStudioConfigInputSchema,
                description: "Hard-delete a studio config and its version history (admins only). The active config, the default template and configs with an open load can't be deleted.",
                destructive: true,
            }, async ({ tx, ctx, args, }) => {
                assertAdmin(ctx, "delete configs");
                const config = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                const studio = await getStudio(tx, projectZql, config.studioID);
                if (studio?.activeConfigID === config.id) {
                    throw new ConflictError("This config is active on the studio's bridge. Load another config first.");
                }
                if (config.isDefault) {
                    throw new ConflictError("This is the studio's default template. Make another template the default first.");
                }
                const [loading, requested] = await Promise.all([
                    tx.run(projectZql.studioConfigLoads.where("configID", config.id)),
                    tx.run(projectZql.studioConfigLoads.where("requestedConfigID", config.id)),
                ]);
                const now = Date.now();
                if ([...(loading ?? []), ...(requested ?? [])].some((load) => isConfigLoadOpen(load, now))) {
                    throw new ConflictError("The studio's bridge is loading this config. Wait for the load to finish (or expire) first.");
                }
                // Its temporary gear and re-patches go with it, unless a cable
                // still has to be put back
                const installation = await loadInstallation(tx, projectZql, config.studioID);
                const gear = planScopedCleanup(installation, config.id);
                if (gear.linksKept.length > 0) {
                    throw new ConflictError(`${gear.linksKept.length} re-patch(es) of this config are not restored yet. Restore them and tick them off on the patch sheet first.`);
                }
                await removeGear(tx, ctx, config.studioID, installation, gear);
                const versions = await tx.run(projectZql.studioConfigVersions.where("configID", args.id));
                for (const version of versions ?? []) {
                    await tx.mutate.studioConfigVersions.delete({ id: version.id });
                }
                await tx.mutate.studioConfigs.delete({ id: args.id });
            }),
            restoreVersion: defineProjectMutator({
                input: restoreStudioConfigVersionInputSchema,
                description: "Restore an older version of a config as its new latest version (not for the active config or templates).",
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                const version = await tx.run(projectZql.studioConfigVersions.where("id", args.versionID).one());
                if (!version || version.configID !== args.configID) {
                    throw new NotFoundError("Config version not found");
                }
                const config = await getLockedConfig(tx, projectZql, args.configID, args.studioID);
                await assertConfigWriteAccess(tx, ctx, config);
                if (config.kind === "template") {
                    throw new ValidationError("Templates only change through “Save to template” from a scratch copy.");
                }
                const studio = await getStudio(tx, projectZql, config.studioID);
                if (isConfigLocked(studio, config.id) &&
                    changedBridgeOwnedKeys(config.configData, version.configData)
                        .length > 0) {
                    throw new ConflictError(CONFIG_LOCK_MESSAGE);
                }
                const versions = await recentVersions(tx, projectZql, config.id);
                const next = Math.max(Number(config.version), ...versions.map((row) => Number(row.version))) + 1;
                await insertVersion(tx, config, next, version.configData, {
                    baseVersion: Number(config.version),
                });
            }),
            setDefaultTemplate: defineProjectMutator({
                input: setDefaultStudioTemplateInputSchema,
                description: "Make a studio template the studio's default (preselected for new configs, loaded by Discard).",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const config = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                if (config.kind !== "template") {
                    throw new ValidationError("Only templates can be the default");
                }
                const templates = await tx.run(projectZql.studioConfigs
                    .where("studioID", config.studioID)
                    .where("kind", "template"));
                // Clear the old default first (one default per studio, enforced
                // by a unique index)
                for (const template of templates ?? []) {
                    if (template.isDefault && template.id !== config.id) {
                        await tx.mutate.studioConfigs.update({
                            id: template.id,
                            isDefault: false,
                        });
                    }
                }
                await tx.mutate.studioConfigs.update({
                    id: config.id,
                    isDefault: true,
                });
            }),
            keep: defineProjectMutator({
                input: keepStudioScratchInputSchema,
                description: "Keep a scratch copy: turn it into a named config or studio template. It is no longer cleaned up and loses its link to the original. The active scratch copy can only become a config (templates are never pushed into).",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const config = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                if (config.kind !== "scratch") {
                    throw new ValidationError("Only scratch copies can be kept");
                }
                if (args.kind === "template") {
                    // The bridge pushes into the active config; templates are never
                    // pushed into, so every live change would be refused
                    const studio = await getStudio(tx, projectZql, config.studioID);
                    if (studio?.activeConfigID === config.id) {
                        throw new ConflictError("The bridge runs this scratch copy. Keep it as a config, or load another config first to keep it as a template.");
                    }
                }
                let isDefault = false;
                if (args.kind === "template") {
                    const templates = await tx.run(projectZql.studioConfigs
                        .where("studioID", config.studioID)
                        .where("kind", "template"));
                    isDefault = !(templates ?? []).some((t) => t.isDefault);
                }
                await tx.mutate.studioConfigs.update({
                    id: config.id,
                    kind: args.kind,
                    name: args.name,
                    kept: true,
                    isDefault,
                    sourceConfigID: null,
                    sourceVersion: null,
                    projectID: args.kind === "config" ? (args.projectID ?? null) : null,
                    updatedAt: Date.now(),
                });
            }),
            saveToOriginal: defineProjectMutator({
                input: saveStudioScratchToOriginalInputSchema,
                description: "Save a scratch copy's current state as the next version of its original (“Save to template” for templates). If the original changed since the copy was made, or it is locked as the active config, a conflict version is stored instead.",
            }, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const scratch = await getLockedConfig(tx, projectZql, args.id, args.studioID);
                if (scratch.kind !== "scratch" || !scratch.sourceConfigID) {
                    throw new ValidationError("Only scratch copies with an original can be saved to it");
                }
                const original = await getConfig(tx, projectZql, scratch.sourceConfigID);
                const studio = await getStudio(tx, projectZql, original.studioID);
                const versions = await recentVersions(tx, projectZql, original.id);
                const locked = isConfigLocked(studio, original.id);
                const plan = planConfigWrite({
                    source: "cloud",
                    headVersion: Number(original.version),
                    // A locked original only takes it as a conflict
                    baseVersion: locked ? -1 : (scratch.sourceVersion ?? null),
                    versions: versions.map(toVersionInfo),
                    now: Date.now(),
                    // Saving is a deliberate step: always a version of its own
                    coalesceMs: 0,
                });
                if (plan.kind === "new") {
                    await insertVersion(tx, original, plan.version, scratch.configData, {
                        baseVersion: Number(original.version),
                    });
                    await tx.mutate.studioConfigs.update({
                        id: scratch.id,
                        sourceVersion: plan.version,
                    });
                }
                else if (plan.kind === "conflict") {
                    await insertVersion(tx, original, plan.version, scratch.configData, {
                        baseVersion: scratch.sourceVersion ?? null,
                        conflictBaseVersion: locked
                            ? Number(original.version)
                            : plan.conflictBaseVersion,
                    });
                }
                else {
                    throw new Error(`Unexpected write plan ${plan.kind}`);
                }
            }),
            resolveConflict: defineProjectMutator({
                input: resolveStudioConfigConflictInputSchema,
                description: "Decide about a conflict version: keep it (it becomes the config's current version) or keep the current version (the conflict is dismissed and stays in the history).",
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                const config = await getLockedConfig(tx, projectZql, args.configID, args.studioID);
                await assertConfigWriteAccess(tx, ctx, config);
                const version = await tx.run(projectZql.studioConfigVersions.where("id", args.versionID).one());
                if (!version ||
                    version.configID !== config.id ||
                    version.conflictBaseVersion == null ||
                    version.conflictResolvedAt != null) {
                    throw new NotFoundError("Open conflict version not found");
                }
                const now = Date.now();
                if (args.keep === "conflict") {
                    const studio = await getStudio(tx, projectZql, config.studioID);
                    // The bridge holds a conflict it pushed itself; anything else
                    // would change the active config behind the bridge's back
                    if (version.source !== "bridge" &&
                        isConfigLocked(studio, config.id) &&
                        changedBridgeOwnedKeys(config.configData, version.configData)
                            .length > 0) {
                        throw new ConflictError(CONFIG_LOCK_MESSAGE);
                    }
                    await tx.mutate.studioConfigs.update({
                        id: config.id,
                        configData: version.configData,
                        version: Number(version.version),
                        updatedAt: now,
                    });
                }
                await tx.mutate.studioConfigVersions.update({
                    id: version.id,
                    conflictResolvedAt: now,
                });
            }),
            removeTemporaryGear: defineProjectMutator({
                input: removeStudioConfigGearInputSchema,
                description: "Remove the temporary gear and re-patches of a config that is not in effect: only re-patches whose restore is ticked off on the patch sheet (or that were never set up), and gear no remaining re-patch touches. Unticked re-patches stay as pending restore.",
                destructive: true,
            }, async ({ tx, ctx, args, }) => {
                assertConfigEditor(ctx);
                const config = await getLockedConfig(tx, projectZql, args.configID, args.studioID);
                await assertConfigWriteAccess(tx, ctx, config);
                const owner = effectiveConfigIDOf(config) ?? config.id;
                if ((await studioEffectiveConfigID(tx, projectZql, config.studioID)) ===
                    owner) {
                    throw new ConflictError("This config's gear and re-patches are in effect on the studio. Load another config first.");
                }
                const installation = await loadInstallation(tx, projectZql, config.studioID);
                const plan = planScopedCleanup(installation, owner);
                await removeGear(tx, ctx, config.studioID, installation, plan);
            }),
            forceUnlock: defineProjectMutator({
                input: forceUnlockStudioConfigInputSchema,
                description: "Admins only, while the studio's bridge is offline: unlock the active config so it can be edited in the cloud. The bridge hears about it when it reconnects; its next push becomes a conflict version if the config changed.",
            }, async ({ tx, ctx, args, }) => {
                assertAdmin(ctx, "force-unlock a config");
                await lockStudios(tx, [args.studioID]);
                const studio = await getStudio(tx, projectZql, args.studioID);
                if (!studio)
                    throw new NotFoundError("Studio not found");
                if (!studio.activeConfigID) {
                    throw new ValidationError("The studio has no active config");
                }
                const status = await tx.run(projectZql.studioBridgeStatus
                    .where("studioID", args.studioID)
                    .one());
                if (bridgeOnlineState(status?.lastSeenAt ?? null).online) {
                    throw new ConflictError("The bridge is online. Load another config instead of unlocking.");
                }
                await tx.mutate.studios.update({
                    id: args.studioID,
                    activeConfigUnlockedAt: Date.now(),
                });
            }),
        },
    };
}
export const studioConfigDomainDescriptions = describeDomains({
    studioConfigs: "Versioned studio configs, studio templates and scratch copies (layouts, device states, Dante). New configs are copies; loads go through POST /api/studios/:id/load.",
    studioConfigLoads: "Loads of a config onto a studio's bridge, with the result the bridge reported",
    mediaAssets: "ATEM media pool content (stills) stored in S3 by sha256, per studio; configs reference it by hash",
    playoutRunLogs: "Append-only proof of play: playout items the bridge played, skipped or failed, per config and project",
});
